import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { IconInfoCircle } from "@tabler/icons-react";
import { MToggle } from "@/components/ui/atoms/MToggle";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import styles from "./CriticalReminderToggle.module.css";

interface CriticalReminderToggleProps {
  checked: boolean;
  onChange: (value: boolean) => void;
}

const renderContent = (text: string) =>
  text.split("\n\n").map((block, i) => {
    const lines = block.split("\n");
    const bullets = lines.filter((l) => l.trim().startsWith("•"));
    const prose = lines
      .filter((l) => !l.trim().startsWith("•"))
      .join(" ")
      .trim();

    return (
      <div key={i} className={i < text.split("\n\n").length - 1 ? "mb-4" : ""}>
        {prose && <p className={bullets.length ? "mb-2" : ""}>{prose}</p>}
        {bullets.length > 0 && (
          <ul className={styles.bulletList}>
            {bullets.map((line, j) => (
              <li key={j} className={styles.bulletItem}>
                {line.replace(/^•\s*/, "")}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  });

const CriticalReminderToggle: React.FC<CriticalReminderToggleProps> = ({
  checked,
  onChange,
}) => {
  const { t } = useTranslation();
  const [showInfo, setShowInfo] = useState(false);

  return (
    <>
      <div className="flex items-center justify-between border-gray-100">
        <div className="flex items-center gap-1.5 flex-1 min-w-0 pr-4">
          <p className="text-[15px] font-medium text-gray-900">
            {t("reminders.critical.toggleLabel")}
          </p>
          <button
            type="button"
            onClick={() => setShowInfo(true)}
            aria-label={t("reminders.critical.learnMoreTitle")}
            className="flex items-center justify-center w-5 h-5 rounded-full bg-[#edf3fc] flex-shrink-0"
          >
            <IconInfoCircle size={16} color="#4c189a" />
          </button>
        </div>
        <MToggle
          checked={checked}
          onChange={onChange}
          ariaLabel={t("reminders.critical.toggleLabel")}
        />
      </div>

      <FooterDrawer
        open={showInfo}
        onClose={() => setShowInfo(false)}
        title={t("reminders.critical.learnMoreTitle")}
        showClose
        height="45vh"
      >
        <div className={styles.learnMoreContent}>
          {renderContent(t("reminders.critical.learnMoreContent"))}
        </div>
      </FooterDrawer>
    </>
  );
};

export default CriticalReminderToggle;
